import * as React from 'react';
import { Label } from '../primitives';
import { cn } from '../../lib/utils';

export interface FormFieldProps {
  /** Field label text. */
  label?: React.ReactNode;
  /** id of the control this label points at. */
  htmlFor?: string;
  /** Adds a required marker next to the label. */
  required?: boolean;
  /** Error message — takes precedence over `help`. */
  error?: string;
  /** Hint text shown beneath the control when there is no error. */
  help?: React.ReactNode;
  /** Content rendered on the right of the label row (e.g. a "Forgot password?" link). */
  labelRight?: React.ReactNode;
  /**
   * Place `labelRight` immediately after the label instead of at the far end
   * of the row. A character counter reads as part of the field's name
   * ("Story · 218/500"); a "Forgot password?" link does not.
   */
  labelRightInline?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Shared layout for a labelled form control: label row + control + error/help.
 * Keeps every migrated form field visually consistent.
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  htmlFor,
  required,
  error,
  help,
  labelRight,
  labelRightInline,
  className,
  children,
}) => (
  <div className={cn('space-y-1.5', className)}>
    {(label || labelRight) && (
      <div
        className={cn(
          'flex items-center gap-1.5',
          labelRightInline ? 'justify-start' : 'justify-between gap-2',
        )}
      >
        {label ? (
          <Label htmlFor={htmlFor}>
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </Label>
        ) : (
          <span />
        )}
        {labelRight}
      </div>
    )}
    {children}
    {/* Spike scale: help and error sit at 11.5px under the control, not at the
        same 14px as the value inside it. */}
    {error ? (
      <p className="text-[11.5px] leading-4 text-destructive">{error}</p>
    ) : help ? (
      <p className="text-[11.5px] leading-4 text-muted-foreground">{help}</p>
    ) : null}
  </div>
);
